import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { 
  User, 
  Settings, 
  MapPin, 
  Shield, 
  Bell, 
  CreditCard, 
  Heart,
  ArrowRight,
  ArrowLeft
} from "lucide-react";

const UserSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate("/login");
    return null;
  }

  const settingsOptions = [
    {
      title: "My Profile",
      description: "Manage your personal information, addresses, and account details",
      icon: User,
      path: "/profile",
      primary: true
    },
    {
      title: "Account Security",
      description: "Update password, email, and security settings",
      icon: Shield,
      path: "/profile?tab=security",
      primary: true
    },
    {
      title: "Addresses",
      description: "Manage your shipping and billing addresses",
      icon: MapPin,
      path: "/profile?tab=addresses",
      primary: false
    },
    {
      title: "Notifications",
      description: "Configure email and push notification preferences",
      icon: Bell,
      path: "#",
      primary: false,
      disabled: true,
      comingSoon: true
    },
    {
      title: "Payment Methods",
      description: "Manage your saved payment methods and billing",
      icon: CreditCard,
      path: "#",
      primary: false,
      disabled: true,
      comingSoon: true
    },
    {
      title: "Wishlist",
      description: "View and manage your saved items",
      icon: Heart,
      path: "#",
      primary: false,
      disabled: true,
      comingSoon: true
    }
  ];
  return (
    <>
      <SEOHead
        title="Account Settings - Bahama Marketplace"
        description="Manage your Bahama Marketplace account settings, security, notifications, and preferences."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">
                  Manage your account settings and preferences.
                </p>
              </div>
            </div>

            {/* Quick Profile Summary */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{user.email}</h3>
                      <p className="text-sm text-muted-foreground">
                        Account created {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => navigate("/profile")}>
                    Edit Profile
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Settings Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              {settingsOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Card 
                    key={option.title}
                    className={`transition-all hover:shadow-md ${
                      option.primary ? 'ring-1 ring-primary/20' : ''
                    } ${
                      option.disabled ? 'opacity-60' : 'cursor-pointer hover:shadow-lg'
                    }`}
                    onClick={() => {
                      if (!option.disabled && option.path !== '#') {
                        navigate(option.path);
                      }
                    }}
                  >
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-3">
                        <Icon className={`h-5 w-5 ${option.primary ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="flex-1">{option.title}</span>
                        {option.comingSoon ? (
                          <span className="text-xs bg-muted px-2 py-1 rounded-full">
                            Coming Soon
                          </span>
                        ) : (
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{option.description}</CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Quick Actions */}
            <div className="mt-8 pt-8 border-t">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/orders")}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  View Orders
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/seller")}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Seller Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSettings;